import React from 'react'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import { kebabCase } from 'lodash'
import _cx from 'classnames/bind'
import { Text, utils } from '@adc/ui-adc'
import cls from './styles.scss'
const cx = _cx.bind(cls)
const formatMoney = utils.formatters.formatMoney

const YOUR_WINNING_BID_LABEL = 'Your Winning Bid'
const YOUR_BID_LABEL = 'Your Next Bid'
const BUYERS_PREMIUM_LABEL = "Buyer's Premium"
const TOTAL_PURCHASE_PRICE_LABEL = 'Total Purchase Price'
const EARNEST_MONEY_DEPOSIT = 'Earnest Money Deposit'
const SALES_DEPOSIT = 'Sales Deposit'
const YOUR_HIGHEST_BID_LABEL = 'Your Highest Bid'

interface CostViewProps {
  elmId: string
  hasError: boolean
  label: string
  labelColor: string
  labelWeight: 'normal' | 'medium' | 'heavy' | 'black' | undefined
  operator: string
  value: number
  valueBackground: 'grayLight' | 'grayExtraLight'
  valueColor: string
  valueExplanation: string
  valueWeight: 'normal' | 'medium' | 'heavy' | 'black' | undefined
}

export class CostView extends React.Component<CostViewProps> {
  static defaultProps = {
    labelColor: 'gray',
    valueColor: 'gray',
  }

  render() {
    const {
      elmId,
      hasError,
      label,
      labelColor,
      labelWeight,
      operator,
      value,
      valueBackground,
      valueColor,
      valueExplanation,
      valueWeight,
    } = this.props

    return (
      <div className={cx('cost-view')} data-elm-id={elmId}>
        <div>
          <Text
            as='span'
            color={labelColor}
            data-elm-id='label'
            weight={labelWeight}
          >
            {label}
          </Text>
          {valueExplanation && (
            <Text
              className={cx('u-pt-1', 'u-pr-2', 'u-pb-2')}
              color='grayMedium'
              data-elm-id='explanation'
              size='tiny'
            >
              {valueExplanation}
            </Text>
          )}
        </div>
        <div>
          {operator && !hasError && (
            <Text className={cx('u-mr-1')} as='span' color='grayMedium'>
              {operator}
            </Text>
          )}
          <Text
            className={cx('value', kebabCase(valueBackground))}
            as='span'
            color={valueColor}
            data-elm-id='value'
            weight={valueWeight}
          >
            {/* TODO: fix core/utils/formatters/formatMoney to be ts */}
            {hasError ? '–' : formatMoney(value, undefined)}
          </Text>
        </div>
      </div>
    )
  }
}

export const YourBid = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_your_bid'
      label={YOUR_BID_LABEL}
      {...props}
    />
  )
}

export const BuyersPremium = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_buyers_premium'
      label={BUYERS_PREMIUM_LABEL}
      {...props}
    />
  )
}

export const TotalPurchasePrice = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_total_purchase_price'
      label={TOTAL_PURCHASE_PRICE_LABEL}
      {...props}
    />
  )
}

export const EarnestMoneyDeposit = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_earnest_money_deposit'
      label={EARNEST_MONEY_DEPOSIT}
      {...props}
    />
  )
}

export const SalesDeposit = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_sales_deposit'
      label={SALES_DEPOSIT}
      {...props}
    />
  )
}

export const YourWinningBid = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_winning_bid'
      label={YOUR_WINNING_BID_LABEL}
      {...props}
    />
  )
}

export const YourHighestBid = (props: any) => {
  return (
    <CostView
      elmId='cost_calculation_your_highest_bid'
      label={YOUR_HIGHEST_BID_LABEL}
      {...props}
    />
  )
}

export const ConfirmingBid = ({
  bidAmount,
  listing,
}: {
  bidAmount: number
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
}) => {
  const calculations = listing.__methods__.getCostCalculations(bidAmount)

  return (
    <div
      data-elm-id='confirm_bid_calculations'
      className={cx('contains-emphasis')}
    >
      <div className={cx('border-top', 'border-bottom', 'margin-bottom')}>
        <YourBid value={bidAmount} />
        <BuyersPremium operator='+' value={calculations.buyersPremium} />
        <TotalPurchasePrice
          labelWeight='heavy'
          operator='='
          value={calculations.totalPurchasePrice}
          valueBackground='grayExtraLight'
          valueWeight='heavy'
        />
      </div>
      <EarnestMoneyDeposit
        labelColor='grayMedium'
        value={calculations.earnestMoneyDeposit}
        valueColor='grayMedium'
      />
    </div>
  )
}

export const TpsConfirmingBid = ({
  bidAmount,
  listing,
}: {
  bidAmount: number
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
}) => {
  const { buyersPremium, totalPurchasePrice } =
    listing.__methods__.getCostCalculations(bidAmount)

  return (
    buyersPremium && (
      <div
        data-elm-id='confirm_bid_calculations'
        className={cx('contains-emphasis')}
      >
        <div className={cx('border-top', 'border-bottom', 'margin-bottom')}>
          <YourBid value={bidAmount} />
          <BuyersPremium operator='+' value={buyersPremium} />
          <TotalPurchasePrice
            labelWeight='heavy'
            operator='='
            value={totalPurchasePrice}
            valueBackground='grayExtraLight'
            valueWeight='heavy'
          />
        </div>
        <SalesDeposit
          labelColor='grayMedium'
          value={listing.trustee?.online_state_deposit_amount}
          valueColor='grayMedium'
        />
      </div>
    )
  )
}
